/*
Dashboard main component
Yuvraj Singh 2024
*/

import React, { useEffect } from "react";
import {useState, useMemo, useCallback, useRef} from 'react';

import PreviewMap from "./PreviewMap.jsx";
import IndexCardGroup from "./IndexCardGroup.jsx";
import PlotModal from "./PlotModal.jsx";


import { setDefaultViewPort, viewRegion, getJsonData } from "./utils.jsx";
import { defaultViewPort } from "./globals.js";


import MapGL, {Popup, Source, Layer, LinearInterpolator, WebMercatorViewport} from 'react-map-gl';

import bbox from '@turf/bbox';
import statesGeoJson from './data/states.geojson';

// plotting imports
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer } from 'recharts';
import { Modal, Button, Stack } from 'react-bootstrap';

import { Container, Row, Col } from 'react-bootstrap';

import TrendMap from './TrendMap.jsx';
import TrendPlot from './TrendPlot.jsx';
import TimeSeriesPlot from './TimeSeriesPlot.jsx';
import { get } from "jquery";

import { Tooltip, OverlayTrigger, Popover } from 'react-bootstrap';

const collapseIcon = ({width=26, height=26}) => (
    <svg width={width} height={height} viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.25 14.75V25.25H7.75V18.25H0.75V14.75H11.25ZM18.25 0.75V7.75H25.25V11.25H14.75V0.75H18.25Z" fill="black"/>
    </svg>
);

const expandIcon = ({width=26, height=26}) => (
    <svg width={width} height={height} viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M0.75 25.25V14.75H4.25V21.75H11.25V25.25H0.75ZM21.75 11.25V4.25H14.75V0.75H25.25V11.25H21.75Z" fill="black"/>
</svg>
);

const InfoIcon = ({width=42, height=42}) => (
    <svg width={width} height={height} viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19.25 29.75H22.75V19.25H19.25V29.75ZM21 15.75C21.4958 15.75 21.9118 15.582 22.2478 15.246C22.5838 14.91 22.7512 14.4947 22.75 14C22.7488 13.5053 22.5808 13.09 22.246 12.754C21.9112 12.418 21.4958 12.25 21 12.25C20.5042 12.25 20.0888 12.418 19.754 12.754C19.4192 13.09 19.2512 13.5053 19.25 14C19.2488 14.4947 19.4168 14.9106 19.754 15.2478C20.0912 15.5849 20.5065 15.7523 21 15.75ZM21 38.5C18.5792 38.5 16.3042 38.0403 14.175 37.121C12.0458 36.2017 10.1938 34.9551 8.61875 33.3813C7.04375 31.8074 5.79717 29.9553 4.879 27.825C3.96084 25.6947 3.50117 23.4197 3.5 21C3.49884 18.5803 3.9585 16.3053 4.879 14.175C5.7995 12.0447 7.04609 10.1926 8.61875 8.61875C10.1914 7.04492 12.0435 5.79833 14.175 4.879C16.3065 3.95967 18.5815 3.5 21 3.5C23.4185 3.5 25.6935 3.95967 27.825 4.879C29.9565 5.79833 31.8086 7.04492 33.3813 8.61875C34.9539 10.1926 36.2011 12.0447 37.1228 14.175C38.0444 16.3053 38.5035 18.5803 38.5 21C38.4965 23.4197 38.0368 25.6947 37.121 27.825C36.2052 29.9553 34.9586 31.8074 33.3813 33.3813C31.8039 34.9551 29.9518 36.2023 27.825 37.1228C25.6982 38.0433 23.4232 38.5023 21 38.5ZM21 35C24.9083 35 28.2188 33.6438 30.9313 30.9313C33.6438 28.2188 35 24.9083 35 21C35 17.0917 33.6438 13.7813 30.9313 11.0688C28.2188 8.35625 24.9083 7 21 7C17.0917 7 13.7813 8.35625 11.0688 11.0688C8.35625 13.7813 7 17.0917 7 21C7 24.9083 8.35625 28.2188 11.0688 30.9313C13.7813 33.6438 17.0917 35 21 35Z" fill="black"/>
    </svg>
);

const PopoverDashboard = (title, text, setShow, id) => (
    <Popover id={id}>
      <Popover.Header as="h4">
      <div className="d-flex justify-content-between">
        {title}
      <a style={{cursor: 'pointer'}}
       onClick={() => setShow(false)}>X</a>
      </div>
      </Popover.Header>
      <Popover.Body>
          {text}
      </Popover.Body>
  </Popover>
);


const trendText = "Trends are calculated using a modified Theil-Sen Slope with significance calculated at the 5% level using a Mann-Kendall test shown with stippling for significant grid boxes. 70% of data must be present for a trend and significance to be calculated."
const timeSeriesText = "Time series are created as the weighted average of available grid boxes for each year. The dashed line represents a fitted 21-term Gaussian filter with padding at the start and end of the time series. Note: This means available grid boxes are not static with time. Averages are calculated for grid boxes with >70% of complete data for the index."


const TitleWithHelp = ({title, text, id, placement, size}) => {
    const [show, setShow] = useState(false);
    const target = useRef(null);
    const overlay = PopoverDashboard(title, text, setShow, id+"-popover");
    //const overlay = tooltip(title, text, id+"-tooltip");
    return(
    <span style={
        {display: 'flex',
        alignItems: 'center',
        marginBottom: '10px'}
    }>
    <span style={{ fontWeight: 'bold',
                marginRight: '4px',
                fontSize: '1.2em' }}>
        {title}
    </span>
    <OverlayTrigger placement={placement} overlay={overlay} show={show}>
        <a 
            className="custom-icon"
            onClick={() => setShow(!show)}
            onHover={() => setShow(!show)}
            ref={target}>
            <InfoIcon width={size} height={size}/>
        </a>
    </OverlayTrigger>
    </span>

)};

/*
Data Acknowledgement Card
*/
const AcknowledgementCard = () => {
    return (
        <div className="card">
            <div className="card-body">
                <h5 className="card-title">Data Acknowledgement</h5>
                <p className="card-text">
                    The data used in this dashboard is sourced from the Australian Government Bureau of Meteorology.
                </p>
            </div>
        </div>
    );
}

/*
Controls
*/

const SelectMenu = ({options, selected, label, handleChange}) => {
    return (
        <>
        <label>{label}</label>
        <select value={selected.value}
        onChange={handleChange} className="custom-select">
            {options.map((option, i) => (
                <option key={i} value={option.value}
                >{option.label}</option>
                ))}
        </select>
        </>
    );
};




// TODO: Format the form to look good and add a submit button
const Sidebar = (props) => {
    const {viewport, setViewport, region, handleRegionChange, 
        selectedIndex, handleIndexChange, indexOptions, stateOptions, onSubmit, LoadingPlot, downloadDashboardAsPng} = props;
    
    
    return (
        <div id="dashboard-controls">
            
            <div className="form-group">
                <div className="form-row">
                <Col md={6}>
                    <SelectMenu
                    options={indexOptions}
                    selected={selectedIndex.value}
                    label={"Select Index"}
                    handleChange={(e) => handleIndexChange(e)}
                    />
                    
                </Col>
                <Col md={6}>
                    <SelectMenu
                    name = "selectedRegion"
                    options={stateOptions}
                    selected={region.selectedRegion}
                    label={"Select Region"}
                    handleChange={(e) => handleRegionChange(e)}
                    />         
                </Col>     
                </div>
                
                
        </div>
        </div>
    );
}
/*

<div className="form-row">
                <div class="alert alert-warning mt-2" role="alert">
          The number of stations change over time, this may introduce a coverage effect in the time-series. For example, many stations were added to the network post 1958 compared to prior years. This could potentially introduce inconsistencies in the time-series and trend values should be used with caution.

        </div>
            </div>
            */

/*
Dashboard page components visual
*/


const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

export default function Dashboard() {    
    // Presumably this is the data that will be used to populate the map
    // Need to add loading by endpoint maybe TODO: backend task
    const indexOptions = [
        {label: 'Hottest Day (TXx)', value: 'TXx'},
        {label: 'Coldest Night (TNn)', value: 'TNn'},
        {label: 'Maximum 1-day Precipitation (Rx1day)', value: 'Rx1day'},
        {label: 'Total Precipitation (PRCPTOT)', value: 'PRCPTOT'},
    ];
    const stateOptions = [
        {label: 'Australia', value: 'AUS'},
        {label: 'New South Wales/ ACT', value: 'NSW'},
        {label: 'Victoria', value: 'VIC'},
        {label: 'Queensland', value: 'QLD'},
        {label: 'South Australia', value: 'SA'},
        {label: 'Western Australia', value: 'WA'},
        {label: 'Tasmania', value: 'TAS'},
        {label: 'Northern Territory', value: 'NT'},
    ];

    const defaultIndexCards = [
        {indexLabel: 'Hottest Day', indexName: 'TXx', trendValue: '0.1', units: '°C/year', indexDefinition: "No description available. Go to climdex.org/learn/indices"},
        {indexLabel: 'Coldest Night', indexName: 'TNn', trendValue: '-0.1', units: '°C/year', indexDefinition: "No description available. Go to climdex.org/learn/indices"},
        {indexLabel: 'Max 1-day Precipitation', indexName: 'Rx1day', trendValue: '0.1', units: 'mm/year', indexDefinition: "No description available. Go to climdex.org/learn/indices"},
        {indexLabel: 'Total Precipitation', indexName: 'PRCPTOT', trendValue: '0.1', units: 'mm/year', indexDefinition: "No description available. Go to climdex.org/learn/indices"},]

    const [indexCards, setIndexCards] = useState(defaultIndexCards);
    

    const [viewport, setViewport] = useState(defaultViewPort);
    
    const [selectedIndex, setSelectedIndex] = useState({
        label: 'Hottest Day (TXx)',
        value: 'TXx'});

    const handleIndexChange = (e) => {
        console.log("Selected Index Change: ", e.target.value)
        const newLabel = indexOptions.find(
            option => option.value === e.target.value).label;
        setSelectedIndex({label: newLabel, value: e.target.value});
        
    }

   const [region, setRegion] = useState({
    selectedRegionType: 'default',
    selectedRegion: {label: 'Australia', value: 'AUS'},
    selectedFeature: null,
    });

    const handleSelectRegionChange = (e) => {
        console.log("Selected Region Change: ", e.target.value)
        // find label by searching for corresponding value in stateOptions
        const newLabel = stateOptions.find(
            option => option.value === e.target.value).label;
        updateRegion(e, null, {label: newLabel, value: e.target.value});
        
    }

    const handleRegionClick = (e) => {
        const selectedFeature = e.features[0];
        if (selectedFeature) {
            console.log("Clicked map Selected Feature: ", selectedFeature.properties.STATE_NAME, selectedFeature.properties.STATE_VALUE)
            updateRegion(e, selectedFeature, {label: selectedFeature.properties.STATE_NAME, value: selectedFeature.properties.STATE_VALUE});
            getJsonData(selectedIndex.value, selectedFeature.properties.STATE_VALUE).then(data => {
                setModalContent(data);
                setIsModalOpen(true);
            });
            // No other options currently for region type
        }
    }

    const updateRegion = (e, selectedFeature, newRegion) => {
        // if no selectedFeature, then it's a select menu change find the corresponding feature
        if (selectedFeature === null) {
            console.log("Selected feautre is null (1st check)")
            selectedFeature = statesGeoJson.features.find(
                feature => feature.properties.STATE_VALUE === newRegion.value
            );
        }
        console.log("Selected Feature: ", selectedFeature, "Region: ", newRegion)
        
        // if selectedFeature is still null then they have picked australia
        if (selectedFeature) {
        // Add this when dealign with different maps
        //if (region.selectedRegionType === 'default') {
            
            setRegion({
                ...region,
                selectedRegion: {label: newRegion.label, value: newRegion.value},
                selectedFeature: selectedFeature
            });
            console.log("Region is set to: ", region.selectedRegion)
            viewRegion(e, selectedFeature, viewport, setViewport);


        
        } else {
            console.log("got default check to aus")
            // Picked Australia * there is probably a better way to do this *
            setDefaultViewPort(setViewport);
            setRegion({
                ...region,
                selectedRegion: {label: 'Australia', value: 'AUS'},
            });
        }
    };


    const [LoadingPlot, setLoadingPlot] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState('');
    const onSubmit = async () => {
        console.log("Submit clicked")
        console.log("Selected Index: ", selectedIndex.value)
        console.log("Selected Region: ", region.selectedRegion)
        const filePrefix = "AGCD_"
        const fileSuffix = "_ANN_1910-2022.json"
        // Construct the URL to the JSON file in the public folder
        const fileName = `${filePrefix}${selectedIndex.value}_${region.selectedRegion.value}${fileSuffix}`;
       
        
        const fileUrl = `data/json/${fileName}`;
        setLoadingPlot(true);
        // Fetch the JSON file
        // TODO: Use getJsonData function from utils
        fetch(fileUrl)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                setLoadingPlot(false);
                return response.json();
            })
            .then(data => {
                setLoadingPlot(false);
                // Here you can handle the JSON data
                console.log("Opening Modal"); // Check if this gets logged
                setModalContent(data)
                setIsModalOpen(true);
            })
            .catch(error => {
                console.error("Error fetching JSON file:", error);
                setLoadingPlot(false);
            });
    };

    useEffect(() => {
        // Load the default data
        onSubmit();
    }
    , [selectedIndex, region]);

    const [isDashboardExpanded, setIsDashboardExpanded] = useState(false);
    const [ifDecade, setIfDecade] = useState(true);

    

    // Compute class names based on the state
    const mapClassNames = isDashboardExpanded ? "col-12" : "col-sm-7 col-12";
    const sidebarClassNames = isDashboardExpanded ? "col-12" : "col-sm-5 col-12";
    const containerClassNames = isDashboardExpanded ? "full-container" : "condensed-container";


    const downloadDashboardAsPng = () => {
        const node = document.getElementById('dashboard-container');

        toPng(node, { cacheBust: true })
            .then((dataUrl) => {
                download(dataUrl, 'dashboard.png');
            })
            .catch((err) => {
                console.error('Failed to export the dashboard to image', err);
            });
    };
    // TODO: FIX DEFAULT VIEWPORT
    return (
        <>
            <section id="overview">
                
                <p>Understanding climate extremes is crucial in a rapidly changing world. Our dashboard is designed to help researchers and enthusiasts alike to monitor and analyze key climatic trends. Focused on four pivotal indices—Hottest Day, Coldest Night, Maximum 1-Day Precipitation, and Total Precipitation—it provides a clear view of climate patterns from 1910 onwards.</p>
                <p>Interactive time series and trend maps offer insights into over a century's worth of data, illustrating how these extremes have evolved both temporally and spatially.  Our dashboard simplifies complex climatological data to help with research, policy development, or education. </p>

                <p>Find out more about the <a href="/learn/datasets/#dataset-AGCD" target="_blank">data used for this dashboard</a>. Or submit any <a href="https://forms.gle/KLd8PhDe6EwULoBF9" target="_blank">feedback and suggestions.</a></p>
            
                
            </section>
            <section className="main">
            <span style={{ fontWeight: 'bold',
                marginRight: '4px',
                fontSize: '1.4em' }}>Australian climate extreme trends (1910-2022)</span>
                <IndexCardGroup indexCards={indexCards} />
            </section>
            <section className="main">
            
            <div className="d-flex">
            <span style={{ fontWeight: 'bold',
                marginRight: '4px',
                fontSize: '1.4em' }} className="mr-1"> Interactive Dashboard </span>
                <a className="custom-icon"
                    onClick={() => setIsDashboardExpanded(!isDashboardExpanded)}>
                        {isDashboardExpanded ?
                            <button>Collapse</button> :
                            <button>Expand</button>}
                </a>
            </div>
            
            <div id="dashboard-container" className={containerClassNames}>
                 
                <div id="dashboard-sidebar" className={sidebarClassNames}>
                    <div className="my-0">
                        <Sidebar
                        viewport={viewport}
                        setViewport={setViewport}
                        region={region}
                        handleRegionChange={handleSelectRegionChange}
                        selectedIndex={selectedIndex}
                        handleIndexChange={handleIndexChange}
                        indexOptions={indexOptions}
                        stateOptions={stateOptions}
                        onSubmit={onSubmit}
                        LoadingPlot={LoadingPlot}
                        downloadDashboardAsPng={downloadDashboardAsPng}
                        />
                        <TitleWithHelp
                        title={"Time Series"}
                        text={timeSeriesText}
                        id={"time-series"}
                        placement={"right-end"}
                        size={24}
                        />
                        <TimeSeriesPlot
                        data={modalContent}
                        ifDecade={ifDecade}/>
                        
                    </div>
                </div>
                <div id="dashboard-map-container" className={mapClassNames}>

                    <TitleWithHelp
                    title={"Trend Map"}
                    text={trendText}
                    id={"trend"}
                    placement={"right-end"}
                    size={24}
                    />
                    <div id="dashboard-map">    
                        <TrendPlot
                        data={modalContent}
                        viewport={viewport}
                        setViewport={setViewport}
                        ifDecade={ifDecade}
                        />
                    </div>
                </div>
                <div class="alert alert-warning mt-3 mb-3" role="alert">
          The number of stations change over time, this may introduce a coverage effect in the time-series. For example, many stations were added to the network post 1957 compared to prior years. This could potentially introduce inconsistencies in the data and the <strong>time-series and trend values should be used with caution</strong>. Areas with low data coverage are shown in white. Please contact the climdex team if you have any questions.

        </div>
                
                
            </div>
            
            

            </section>
            
        </>
    );
};



  